import { Box, Flex, Center, Input, Spinner } from "@chakra-ui/react";
import { Text } from "@chakra-ui/react";
import Card from "@root/components/enrollment-card/card";
import { useTranslation } from "react-i18next";
import SearchInput from "@root/components/common/SearchInput";
import { StyledSelect } from "@root/components/common/select/StyledSelect";
import { useNavigate } from "react-router-dom";
import { COURSE_REQUIREMENT, RECORDS_PER_PAGE_COURSES } from "@root/constants";
import { useCourseManagementContext } from "@root/context/course-management-context";
import { useOrganizationBranding } from "@root/context/organization-branding-context";
import { useEffect, useState, useRef } from "react";
import { ROUTE_NAMES } from "@root/constants/navigation-routes";
import { Paginate } from "@root/components/pagination";
import { useUnauthenticatedCoursesTourGuide } from "@hooks/useUnauthenticatedCoursesTourGuide";
import { TourGuide, RestartTourButton } from "@components/tour-guide";
import { SearchIcon } from "@chakra-ui/icons";
import CustomSelect from "@root/components/common/CustomSelect";
import useMediaQ from "@root/hooks/useMediaQ";

const UnauthenticatedCoursesPages = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { sm, isMobileView } = useMediaQ();
  const isMobile = !sm;

  // Tour guide integration
  const { unauthenticatedCoursesTourGuide, courseCardRef } =
    useUnauthenticatedCoursesTourGuide();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedRequirement, setSelectedRequirement] = useState<string>("");
  const isInitialMount = useRef(true);
  const isFiltersInitialMount = useRef(true);
  const lastFetchRef = useRef<string>("");

  const {
    search,
    setSearch,
    setPage,
    page,
    pageCount,
    freeCoursesData,
    fetchFreeCourses,
    fetchFreeCategories,
    freeCategoriesData,
  } = useCourseManagementContext();
  const { slug } = useOrganizationBranding();

  // Fetch categories only once on mount
  useEffect(() => {
    if (slug == null) return;
    fetchFreeCategories({ slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (isFiltersInitialMount.current) {
      isFiltersInitialMount.current = false;
      return;
    }
    if (page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, selectedRequirement]);

  // Main effect: Fetch courses when search, page, or filters change
  useEffect(() => {
    if (isInitialMount.current && slug != null) {
      isInitialMount.current = false;
      const initialKey = `init-${slug}`;
      if (lastFetchRef.current !== initialKey) {
        lastFetchRef.current = initialKey;
        fetchFreeCourses({ slug });
      }
      return;
    }

    const categoryIds: Set<string> = new Set();
    const subcategoryIds: string[] = [];
    selectedCategories?.forEach((val) => {
      if (typeof val === "string") {
        if (val?.includes(":")) {
          const parts = val?.split(":");
          const parentId = parts[0];
          const subId = parts[1];
          if (parentId) categoryIds?.add(parentId);
          if (subId) subcategoryIds?.push(subId);
        } else {
          categoryIds?.add(val);
        }
      }
    });

    const payload: any = { slug };
    if (categoryIds.size > 0) {
      payload.categoryId = Array.from(categoryIds).join(",");
    }
    if (subcategoryIds.length > 0) {
      payload.subcategoryId = subcategoryIds.join(",");
    }
    if (selectedRequirement) {
      payload.requirement = selectedRequirement;
    }

    const fetchKey = `${search || ""}-${page}-${selectedCategories.join(
      ",",
    )}-${selectedRequirement}`;
    if (lastFetchRef.current !== fetchKey) {
      lastFetchRef.current = fetchKey;
      fetchFreeCourses(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, selectedCategories, selectedRequirement, slug]);

  const categoriesOptions = freeCategoriesData?.rows?.map((item: any) => ({
    label: item?.title,
    value: String(item?.id),
    children: item?.children?.map((subcat: any) => ({
      label: subcat?.title,
      value: String(subcat?.id),
    })),
  }));

  const getSelectedCategoryIds = () => {
    return selectedCategories
      ?.filter((val) => typeof val === "string")
      ?.map((val) => {
        if (val.includes(":")) {
          return val.split(":").pop();
        }
        return val;
      });
  };
  const selectedCategoryIds = getSelectedCategoryIds();
  const filteredCourses =
    selectedCategoryIds.length > 0
      ? freeCoursesData?.rows?.filter((course: any) => {
          if (course.subcategory && course.subcategory.id) {
            return (
              selectedCategoryIds?.includes(String(course?.category?.id)) ||
              selectedCategoryIds?.includes(String(course?.subcategory?.id))
            );
          } else {
            return selectedCategoryIds?.includes(String(course?.category?.id));
          }
        })
      : freeCoursesData?.rows;

  const requirementOptions = [
    {
      label: t("coursesPage.essentialRequired"),
      value: COURSE_REQUIREMENT.ESSENTIAL,
    },
    {
      label: t("coursesPage.optionalRecommended"),
      value: COURSE_REQUIREMENT.OPTIONAL,
    },
  ];

  const hasActiveFilters =
    search?.trim() !== "" ||
    selectedCategories?.length > 0 ||
    selectedRequirement !== "";

  const noCoursesFound =
    !filteredCourses ||
    filteredCourses.length === 0 ||
    (freeCoursesData?.rows && freeCoursesData.rows.length === 0);

  function renderEmptyState() {
    return (
      <Center flexDirection="column" minH="500px" py={10} px={4} width="100%">
        <Box
          width="240px"
          height="240px"
          bg="linear-gradient(135deg, #F5F5F5 0%, #E8E8E8 100%)"
          borderRadius="16px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mb={6}
          boxShadow="0px 4px 12px rgba(0, 0, 0, 0.05)"
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-20px"
            right="-20px"
            width="100px"
            height="100px"
            bg="rgba(24, 25, 180, 0.05)"
            borderRadius="50%"
          />
          <Box
            position="absolute"
            bottom="-30px"
            left="-30px"
            width="120px"
            height="120px"
            bg="rgba(24, 25, 180, 0.03)"
            borderRadius="50%"
          />
          <Text fontSize="100px" lineHeight="1" opacity={0.3}>
            📚
          </Text>
        </Box>
        <Text
          fontSize="24px"
          fontWeight={600}
          color="#2F2F34"
          mb={2}
          textAlign="center"
        >
          {t("coursesPage.noCoursesFound") || "No Courses Found"}
        </Text>
        <Text
          fontSize="16px"
          fontWeight={400}
          color="#798692"
          textAlign="center"
          maxW="500px"
          lineHeight="1.6"
        >
          {hasActiveFilters
            ? t("coursesPage.noCoursesForFilters") ||
              "No courses match your selected filters. Try adjusting your search or filter criteria."
            : t("coursesPage.noCoursesAvailable") ||
              "No courses are available at the moment. Please check back later."}
        </Text>
      </Center>
    );
  }

  // ── Mobile layout ──────────────────────────────────────────────────────────
  if (isMobileView && isMobile) {
    return (
      <Box
        py={4}
        px={4}
        bg="#fafbfc"
        width="100%"
        maxW="100%"
        overflowX="hidden"
      >
        <Box width="100%" maxW="100%">
          <Text fontWeight={600} fontSize={"24px"} color={"#2F2F34"} mb={4}>
            {t("coursesPage.getStarted")}
          </Text>
          {/* <Text fontSize="16px" fontWeight={400} textColor={"#798692"} mb={6}>
            {t("coursesPage.browseAndEnroll")}
          </Text> */}

          {/* Search + Filters */}
          <Flex flexDirection="column" gap="12px" mb={5}>
            <Box position="relative">
              <Input
                placeholder={t("coursesPage.searchPlaceholder")}
                borderRadius="12px"
                bg="white"
                border="1px solid #E6E9EF"
                pl="40px"
                pr="16px"
                py="12px"
                fontSize="14px"
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setPage(1);
                }}
              />
              <SearchIcon
                position="absolute"
                left="12px"
                top="50%"
                transform="translateY(-50%)"
                color="#9CA3AF"
                boxSize="16px"
              />
            </Box>

            <Flex justifyContent={"end"} gap="12px" flexWrap="wrap">
              <StyledSelect
                placeholder={t("coursesPage.categories")}
                data={categoriesOptions}
                selectedValues={selectedCategories}
                onChange={setSelectedCategories}
                width="100%"
                height="44px"
                borderRadius="12px"
                border="1px solid #E6E9EF"
                bg="white"
                fontSize="14px"
                color="#9CA3AF"
              />
              <CustomSelect
                options={requirementOptions}
                value={selectedRequirement}
                onChange={setSelectedRequirement}
                placeholder={t("coursesPage.requirement")}
                height="44px"
                borderRadius="100px"
                border="1px solid #E6E9EF"
                bg="white"
                fontSize="14px"
                color="#9CA3AF"
              />
            </Flex>
          </Flex>

          {/* Course List */}
          {noCoursesFound ? (
            renderEmptyState()
          ) : (
            <Box
              overflowY="auto"
              overflowX="hidden"
              maxH={"calc(100vh - 265px)"}
            >
              <Flex flexDirection="column" gap="12px" pr="4px">
                {filteredCourses?.map((course: any, index: number) => (
                  <Box key={index} width="100%">
                    <Card
                      course={course}
                      onClick={() =>
                        navigate(
                          `${ROUTE_NAMES.UNAUTHENTICATED_COURSE_DETAIL}/${course?.id}`,
                        )
                      }
                    />
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          {/* Pagination */}
          {pageCount > 0 && (
            <Box mt={1} display="flex" justifyContent="center">
              <Paginate
                marginTop={6}
                page={page}
                count={pageCount}
                pageSize={RECORDS_PER_PAGE_COURSES}
                onPageChange={(newPage: number) => setPage(newPage)}
                variant="ghost"
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────
  return (
    <Box py={4} px={8} bg="#fafbfc" width="100%" maxW="100%" minH="100vh">
      <Box width="100%" mx="auto">
        <Text fontWeight={600} fontSize={"24px"} color={"#2F2F34"} mb={4}>
          {t("coursesPage.getStarted")}
        </Text>
        {/* <Text fontSize="16px" fontWeight={400} textColor={"#798692"} mb={6}>
          {t("coursesPage.browseAndEnroll")}
        </Text> */}
        <Flex pr={5} justifyContent={"space-between"} mb={5}>
          <Box data-tour-item="courses-search">
            <SearchInput
              placeholder={t("coursesPage.searchPlaceholder")}
              width="500px"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                }
              }}
            />
          </Box>
          <Flex gap={4}>
            <Box data-tour-item="courses-category-filter">
              <StyledSelect
                placeholder={t("coursesPage.categories")}
                data={categoriesOptions}
                selectedValues={selectedCategories}
                onChange={setSelectedCategories}
              />
            </Box>
            <Box data-tour-item="courses-requirement-filter">
              <CustomSelect
                options={requirementOptions}
                value={selectedRequirement}
                onChange={setSelectedRequirement}
                placeholder={t("coursesPage.requirement")}
                width="160px"
                height="44px"
              />
            </Box>
          </Flex>
        </Flex>

        {noCoursesFound ? (
          renderEmptyState()
        ) : (
          <Flex
            as="div"
            sx={{
              display: "grid",
              gridTemplateColumns: {
                base: "1fr",
                sm: "repeat(1, 1fr)",
                md: "repeat(2, 1fr)",
                lg: "repeat(2, 1fr)",
                xl: "repeat(3, 1fr)",
                "2xl": "repeat(4, 1fr)",
              },
              gap: "15px",
              width: "100%",
              maxWidth: "100%",
            }}
          >
            {filteredCourses?.map((course: any, index: number) =>
              index === 0 ? (
                <Box key={index} ref={courseCardRef}>
                  <Card
                    course={course}
                    onClick={() => {
                      navigate(
                        `${ROUTE_NAMES.UNAUTHENTICATED_COURSE_DETAIL}/${course?.id}`,
                      );
                    }}
                  />
                </Box>
              ) : (
                <Card
                  key={index}
                  course={course}
                  onClick={() => {
                    navigate(
                      `${ROUTE_NAMES.UNAUTHENTICATED_COURSE_DETAIL}/${course?.id}`,
                    );
                  }}
                />
              ),
            )}
          </Flex>
        )}

        {/* Pagination */}
        {pageCount > 0 && (
          <Box mt={6} display="flex" justifyContent="center">
            <Paginate
              marginTop={6}
              page={page}
              count={pageCount}
              pageSize={RECORDS_PER_PAGE_COURSES}
              onPageChange={(newPage: number) => {
                setPage(newPage);
              }}
              variant="ghost"
            />
          </Box>
        )}
      </Box>

      {/* Restart Tour Button — desktop only */}
      {!unauthenticatedCoursesTourGuide.isActive && (
        <RestartTourButton
          onClick={unauthenticatedCoursesTourGuide.startTour}
          position={{ bottom: "24px", right: "24px" }}
        />
      )}

      {/* Active Tour Guide */}
      {unauthenticatedCoursesTourGuide.isActive &&
        unauthenticatedCoursesTourGuide.currentStepData && (
          <TourGuide
            isActive={unauthenticatedCoursesTourGuide.isActive}
            currentStep={unauthenticatedCoursesTourGuide.currentStepData}
            currentStepIndex={unauthenticatedCoursesTourGuide.currentStep}
            totalSteps={unauthenticatedCoursesTourGuide.totalSteps}
            onNext={unauthenticatedCoursesTourGuide.nextStep}
            onBack={unauthenticatedCoursesTourGuide.previousStep}
            onClose={unauthenticatedCoursesTourGuide.stopTour}
            targetRef={
              unauthenticatedCoursesTourGuide.currentStepData.id ===
              "course-card"
                ? courseCardRef
                : undefined
            }
            targetSelector={
              unauthenticatedCoursesTourGuide.currentStepData.targetSelector
            }
            borderRadius="8px"
          />
        )}
    </Box>
  );
};

export default UnauthenticatedCoursesPages;
